import React, { useState, useEffect } from "react";
import { Pie } from "react-chartjs-2";
import PercentIcon from "@mui/icons-material/Percent";
import Slider from "@mui/material/Slider";
import { Helmet } from "react-helmet";
import SWPPDFGenerator from "../pdf/pdf-swp";
import ReactGA from "react-ga4";
import { Typography, IconButton } from "@mui/material";
import SaveTitleDialog from "../dialog-box/SaveTitleDialog";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import { SWPData } from "../models/swp";
import { saveSWP, updateSWP } from "../Database/swp-db";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../Redux/store";
import { setCurrencySymbol } from "../Redux/currencySlice";
import { useLocation } from "react-router-dom";
import BootstrapTooltip from "../components/tooltip";
import SavedSnackbar from "../utils/saved-snackbar";


const formatCurrency = (value: string) => {
  const numValue = value.replace(/[^0-9]/g, "");
  if (numValue === "") return "";
  return Number(numValue).toLocaleString("en-IN");
};

const SystematicWithdrawalPlan: React.FC<{ swp?: SWPData }> = ({ }) => {
  const location = useLocation();

  const [swpData, setSWPData] = useState<SWPData | null>(null);
  const [initialInvestment, setInitialInvestment] = useState("10,00,000");
  const [investment, setInvestment] = useState(0);

  const [monthlyWithdrawal, setMonthlyWithdrawal] = useState("500");
  const [rate, setRate] = useState("8");
  const [period, setPeriod] = useState("3");
  const [finalBalance, setFinalBalance] = useState<number | null>(0);
  const [totalWithdrawal, setTotalWithdrawal] = useState<number | null>(0);
  const [chartData, setChartData] = useState<any>(null);
  const [showPdfButton, setShowPdfButton] = useState(false);
  const [error, setError] = useState({
    initialInvestment: "",
    monthlyWithdrawal: "",
    rate: "",
    period: "",
  });
  const [showResult, setShowResult] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [icon, setIcon] = useState<React.ReactNode>(<FavoriteBorderIcon />);
  const [title, setTitle] = useState("");
  const currencySymbol = useSelector(
    (state: RootState) => state.country.currencySymbol
  );
  const dispatch = useDispatch();
  const [, setSymbol] = useState(currencySymbol);
  const [savedTitle, setSavedTitle] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [isCalculated, setIsCalculated] = useState(false);


  const handleSubmit = async () => {
    const updatedData: SWPData = {
      initialInvestment: parseFloat(initialInvestment.replace(/,/g, "")),
      monthlyWithdrawal: parseFloat(monthlyWithdrawal.replace(/,/g, "")),
      rate: parseInt(rate),
      period: parseFloat(period.replace(/,/g, "")),
      currencySymbol: currencySymbol,
      title: title,

    };

    if (swpData?.id) {
      await updateSWP(swpData.id, updatedData);
    } else {
      await saveSWP(updatedData);
    }
    setSnackbarMessage("Saved successfully!");
    setSnackbarOpen(true);
    setIcon(<FavoriteIcon style={{ color: "red" }} />);
    handleDialogClose();
    setTitle(updatedData.title);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCalculate();

    }
  };


  useEffect(() => {
    const { swp: passedSwp, currencySymbol: passedCurrencySymbol } = location.state || {};

    if (passedCurrencySymbol) {
      dispatch(setCurrencySymbol(passedCurrencySymbol));
      setSymbol(passedCurrencySymbol);
    } else {
      setSymbol(currencySymbol);
    }

    if (passedSwp) {
      setSWPData(passedSwp);
      setIcon(<FavoriteIcon style={{ color: "red" }} />);
      setInitialInvestment(formatCurrency(passedSwp.initialInvestment.toString()));
      setMonthlyWithdrawal(formatCurrency(passedSwp.monthlyWithdrawal.toString()));
      setRate(passedSwp.rate.toString());
      setPeriod(passedSwp.period.toString());
      setTitle(passedSwp.title);
      setSavedTitle(passedSwp.title || "");
      handleCalculate()
    }
    handleCalculate()


  }, [location.state, swpData]);

  const handleReset = () => {
    ReactGA.event({
      category: "SWP calculator",
      action: "SWP Reset Button",
      label: "SWP Calculator",
    });
    setInitialInvestment("10,00,000");
    setMonthlyWithdrawal("500");
    setRate("8");
    setPeriod("3");
    setFinalBalance(null);
    setTotalWithdrawal(null);
    setChartData(null);
    setError({
      initialInvestment: "",
      monthlyWithdrawal: "",
      rate: "",
      period: "",
    });
    setShowResult(false);
    setShowPdfButton(false);
  };


  const validateInputs = () => {
    const newErrors = {
      initialInvestment: "",
      monthlyWithdrawal: "",
      rate: "",
      period: ""
    };
    let isValid = true;

    const minInvestment = 10000;
    const minWithdrawal = 500;

    if (!initialInvestment) {
      newErrors.initialInvestment = "Investment amount is mandatory.";
      isValid = false;
    } else if (parseFloat(initialInvestment.replace(/,/g, "")) < minInvestment) {
      newErrors.initialInvestment = "Total Investment amount can't be less than" + currencySymbol + "10000.";
      isValid = false;
    }
    if (!monthlyWithdrawal) {
      newErrors.monthlyWithdrawal = "Investment amount is mandatory.";
      isValid = false;
    } else if (parseFloat(monthlyWithdrawal.replace(/,/g, "")) < minWithdrawal) {
      newErrors.monthlyWithdrawal = "Monthly Withdrawal amount can't be less than " + currencySymbol + "500.";
      isValid = false;
    }

    const rateValue = parseFloat(rate.replace(/,/g, ""));
    if (!rate) {
      newErrors.rate = "Interest rate is mandatory.";
      isValid = false;
    } else if (isNaN(rateValue) || rateValue < 1 || rateValue >= 30) {
      newErrors.rate = 'Interest rate must be between 1 and 30.';
      isValid = false;
    }

    if (!period) {
      newErrors.period = "Period unit years is mandatory.";
      isValid = false;
    }

    setError(newErrors);
    return isValid;
  };

  const handleCalculate = () => {
    ReactGA.event({
      category: "SWP calculator",
      action: "SWP Calculate Button",
      label: "SWP Calculator",
    });
    if (!validateInputs()) {
      return;
    }
    setError({
      initialInvestment: "",
      monthlyWithdrawal: "",
      rate: "",
      period: "",
    });

    const initialInvestmentValue = parseFloat(
      initialInvestment.replace(/,/g, "")
    );
    const monthlyWithdrawalValue = parseFloat(
      monthlyWithdrawal.replace(/,/g, "")
    );
    const monthlyRate = parseFloat(rate) / 100 / 12;
    const totalMonths = parseFloat(period) * 12;

    let remainingBalance = initialInvestmentValue;
    let totalWithdrawn = 0;

    for (let i = 0; i < totalMonths; i++) {

      remainingBalance -= monthlyWithdrawalValue;
      totalWithdrawn += monthlyWithdrawalValue;

      remainingBalance *= 1 + monthlyRate;
    }

    setFinalBalance(remainingBalance);
    setTotalWithdrawal(totalWithdrawn);
    setShowResult(true);
    setShowPdfButton(true);
    setInvestment(initialInvestmentValue);
    setIsCalculated (true);

    const pieChartData = [];
    const pieChartLabels = [];

    if (initialInvestmentValue > 0) {
      pieChartData.push(initialInvestmentValue);
      pieChartLabels.push("Total Investment");
    }
    if (totalWithdrawn > 0) {
      pieChartData.push(totalWithdrawn);
      pieChartLabels.push("Total Withdrawn");
    }
    if (remainingBalance > 0) {
      pieChartData.push(remainingBalance);
      pieChartLabels.push("Final Balance");
    }
    const total = pieChartData.reduce((acc, value) => acc + value, 0);
    const percentages = pieChartData.map(value => ((value / total) * 100).toFixed(2));

    setChartData({
      labels: pieChartLabels.map((label, index) => `${label}: ${percentages[index]}%`),
      datasets: [
        {
          data: pieChartData,
          backgroundColor: ["#36A2EB", "#FF6384", "#FFCE56"].slice(0, pieChartData.length),
        },
      ],
    });

  };

  const handleInputChange = (
    setter: React.Dispatch<React.SetStateAction<string>>,
    value: string,
    isDecimalAllowed: boolean = false,
    maxLength: number = 10,
    maxValue: number = 100
  ) => {
    if (value === "") {
      setter(value);
      return;
    }

    const sanitizedValue = isDecimalAllowed
      ? value.replace(/[^0-9.]/g, "").replace(/(\..*?)\..*/g, "$1")
      : value.replace(/[^0-9]/g, "");

    if ((setter === setInitialInvestment || setter === setMonthlyWithdrawal) && sanitizedValue.length <= maxLength) {
      const numericValue = parseFloat(sanitizedValue);
      if (numericValue > 0) {
        setter(formatCurrency(sanitizedValue));
      } else {
        setter("");
      }
    }
    else if ((setter === setRate || setter === setPeriod)) {
      const numericValue = parseFloat(sanitizedValue);
      if (numericValue > 0 && numericValue <= maxValue) {
        setter(sanitizedValue);
      } else {
        setter("");
      }
    }
  };

  const handleNumberInput = (
    e: React.ChangeEvent<HTMLInputElement>,
    setter: React.Dispatch<React.SetStateAction<string>>,
    maxLimit: number,
    decimalLimit: number = 2
  ) => {
    const value = e.target.value;

    if (value === "") {
      setter("");
      return;
    }

    const validValue = value.replace(/[^0-9.]/g, "");
    const parts = validValue.split(".");

    if (parts.length > 1 && parts[1].length > decimalLimit) {
      parts[1] = parts[1].slice(0, decimalLimit);
    }

    const formattedValue = parts.join(".");

    if (parseFloat(formattedValue) > 0 && parseFloat(formattedValue) <= maxLimit) {
      setter(formattedValue);
    } else if (parseFloat(formattedValue) <= 0) {
      setter("");
    }
  };

  const handleSliderChange = (
    setter: React.Dispatch<React.SetStateAction<string>>,
    value: number
  ) => {
    setter(value.toString());
  };

  const handleDialogOpen = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setTitle("");
  };

  const handleCancel = () => {
    handleDialogClose();
  };

  const handleSnackbarClose = (
    event: React.SyntheticEvent | Event,
    reason: "timeout" | "clickaway" | "escapeKeyDown"
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };


  return (
    <div className="container mt-4" onKeyPress={handleKeyPress}>
      <Helmet>
        <title>SWP Calculator</title>
        <meta
          name="description"
          content="Calculate your withdrawal amounts and the remaining balance with our SWP Calculator. Plan periodic withdrawals from your investments while estimating the residual value."
        />
        <link rel="canonical" href="/swp-calculator.html" />
      </Helmet>
      <SavedSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        onClose={handleSnackbarClose}
      />
      <div className="container ">
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-2" style={{ fontFamily: "serif", fontSize: "30px", fontWeight: "bold" }}>
            SWP Calculator
          </h5>
        </div>
        <p> A SWP Calculator helps estimate the amount of money you can withdraw systematically from your investments over a specified period, allowing you to manage your cash flow while considering the expected rate of return.
        </p>
        <div className="row justify-content-center">

          {savedTitle && (
            <Typography variant="h6" style={{ marginLeft: '20px' }}>
              {savedTitle}
            </Typography>
          )}
          <div className="col-md-6">
            <div className="col-12 ">
            </div>
            <div className="card shadow-sm" style={{ width: '100%', height: '580px', margin: '0 auto', overflow: 'auto' }}>
              <div className="card-body">
                <div className="mb-4">
                  <label htmlFor="initialInvestment" className="form-label">
                    Total Investment ({currencySymbol})
                  </label>
                  <input
                    type="text"
                    className={`form-control ${error.initialInvestment ? "is-invalid" : ""
                      }`}
                    id="initialInvestment"
                    value={initialInvestment}
                    onChange={(e) =>
                      handleInputChange(
                        setInitialInvestment,
                        e.target.value,
                        false,
                        8,
                        10000000
                      )
                    }
                  />
                  {error.initialInvestment && <div className="invalid-feedback text-dannger">{error.initialInvestment}</div>}

                  <div className="mb-3 d-flex justify-content-center">
                    <Slider
                      min={10000}
                      max={10000000}
                      step={10000}
                      value={parseFloat(initialInvestment.replace(/,/g, ""))}
                      onChange={(e, newValue) =>
                        handleSliderChange(
                          setInitialInvestment,
                          newValue as number
                        )
                      }
                      valueLabelDisplay="auto"
                      sx={{ width: "95%" }}
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <label htmlFor="monthlyWithdrawal" className="form-label">
                    Monthly Withdrawal Amount ({currencySymbol})
                  </label>
                  {error.monthlyWithdrawal && <div className="invalid-feedback">{error.monthlyWithdrawal}</div>}

                  <input
                    type="text"
                    className="form-control"
                    id="monthlyWithdrawal"
                    value={monthlyWithdrawal}
                    onChange={(e) =>
                      handleInputChange(
                        setMonthlyWithdrawal,
                        e.target.value,
                        false,
                        5,
                        50000
                      )
                    }
                  />

                  <div className="mb-3 d-flex justify-content-center">
                    <Slider
                      min={500}
                      max={50000}
                      step={500}
                      value={parseFloat(monthlyWithdrawal.replace(/,/g, ""))}
                      onChange={(e, newValue) =>
                        handleSliderChange(
                          setMonthlyWithdrawal,
                          newValue as number
                        )
                      }
                      valueLabelDisplay="auto"
                      sx={{ width: "95%" }}
                    />
                  </div>
                  {error.monthlyWithdrawal && (
                    <div className="text-danger">{error.monthlyWithdrawal}</div>
                  )}
                </div>

                <div className="mb-3">
                  <label htmlFor="rate" className="form-label">
                    Expected Annual Return Rate (%)
                  </label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      id="rate"
                      value={rate}
                      onChange={(e) => handleNumberInput(e, setRate, 100, 2)}
                    />
                    <span className="input-group-text">
                      <PercentIcon />
                    </span>


                  </div>
                  {error.rate && (
                    <div className="text-danger">{error.rate}</div>
                  )}
                </div>

                <div className="mb-3">
                  <label htmlFor="period" className="form-label">
                    Time Period (Years)
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="period"
                    value={period}
                    onChange={(e) =>
                      handleInputChange(
                        setPeriod,
                        e.target.value,
                        false,
                        10,
                        100
                      )
                    }
                    max={100}
                  />
                  {error.period && (
                    <div className="text-danger">{error.period}</div>
                  )}
                </div>

                <div className="d-flex justify-content-center " style={{ marginTop: '20px' }}>
                  <button
                    className="btn btn-primary btn-lg "
                    onClick={handleCalculate}

                  >
                    Calculate
                  </button>
                </div>
                <div className='d-flex justify-content-center'>
                  <a
                    onClick={handleReset}
                    style={{ cursor: "pointer", textDecoration: "none" }}
                    className=" pt-2"
                  >
                    Reset
                  </a>
                </div>
              </div>
            </div>
          </div>
          {showResult && (
            <div className="col-md-6">
              <div className="card mt-4 mt-sm-0">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center">

                    <h5 className="card-title ">Result</h5>
                    <BootstrapTooltip title="Save Calculation" placement="top">
                      <IconButton color="secondary" onClick={handleDialogOpen}>
                        {icon}
                      </IconButton>
                    </BootstrapTooltip>
                  </div>

                  <p className="card-text d-flex justify-content-between">
                    <strong>Total Investment:</strong>
                    <span className="text-end">
                      {currencySymbol}{" "}
                      {investment !== null && initialInvestment !== ''
                        ? (investment)
                        : '0'}
                    </span>
                  </p>
                  <p className="card-text">
                    <strong>Total Withdrawal Amount:</strong>{" "}
                    <span className="float-end">
                      {totalWithdrawal !== null
                        ? `${currencySymbol}  ${Math.ceil(totalWithdrawal).toLocaleString("en-IN", {
                        })}`
                        : "${currencySymbol}0"}
                    </span>
                  </p>
                  <p className="card-text">
                    <strong>Final Balance after Withdrawals:</strong>{" "}
                    <span className="float-end">
                      {finalBalance !== null
                        ? `${currencySymbol} ${Math.ceil(finalBalance).toLocaleString("en-IN", {
                          // minimumFractionDigits: 2,
                          // maximumFractionDigits: 2,
                        })}`
                        : "₹0"}
                    </span>
                  </p>
                </div>
              </div>
              {chartData && (
                <div className="card mt-4 mb-3">
                  <div className="card-body">
                    <h5 className="card-title mb-4">Interest Distribution</h5>
                    <div className="d-flex justify-content-center">
                      <div style={{ width: "300px", height: "300px" }}>
                        <Pie data={chartData} />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="d-flex justify-content-end" style={{ paddingLeft: "90px", marginBottom: '10px' }}>
            {showPdfButton && (
              <SWPPDFGenerator
                initialInvestment={initialInvestment}
                monthlyWithdrawal={monthlyWithdrawal}
                rate={rate}
                period={period}
                finalBalance={finalBalance}
                totalWithdrawal={totalWithdrawal}
                isCalculated={isCalculated}
              />
            )}
          </div>
        </div>

        <SaveTitleDialog
          open={dialogOpen}
          onClose={handleDialogClose}
          title={title}
          setTitle={setTitle}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
        />
      </div>
    </div>
  );
};

export default SystematicWithdrawalPlan;
